if (!customElements.get('emergent-pack-info')) {
  customElements.define('emergent-pack-info', class extends HTMLElement {
    connectedCallback() {
      this.cancelled = false;
      this.mount = null;
      this.observedShadow = null;
      this.variantId = null;
      this.amountNode = null;
      this.mainProduct = this.closest('product-info[id^="MainProduct-"]');
      if (this.mainProduct?.dataset.productId !== this.dataset.productId) return;
      this.copy = this.querySelector('[data-pack-copy]');
      this.unit = this.querySelector('[data-pack-unit]');
      this.variants = new Map([...this.querySelectorAll('template[data-pack-variant]')].map((node) => [node.dataset.packVariant, node]));
      this.onVariantChange = this.onVariantChange.bind(this);
      this.mainProduct.addEventListener('change', this.onVariantChange);
      this.mainObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.queuePriceUpdate();
      });
      this.mainObserver.observe(this.mainProduct, {childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'hidden']});
      const input = this.mainProduct.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      if (input) this.showVariant(input.value);
    }
    disconnectedCallback() {
      this.mainProduct?.removeEventListener('change', this.onVariantChange);
      this.mainObserver?.disconnect();
      this.shadowObserver?.disconnect();
      this.cancelled = true;
      this.queued = false;
    }
    onVariantChange(event) {
      const input = event.target;
      if (!input.matches?.('form[data-type="add-to-cart-form"] input[name="id"]') || input.closest('product-info[id^="MainProduct-"]') !== this.mainProduct) return;
      this.showVariant(input.value);
    }
    showVariant(variantId) {
      const nextId = String(variantId);
      if (this.variantId !== nextId) {
        this.staleMount = this.mount;
        this.staleAmountNode = this.amountNode;
        this.staleAmountText = this.amountNode?.textContent;
        this.awaitingShadowRefresh = true;
      }
      this.variantId = nextId;
      const template = this.variants.get(nextId);
      this.hidden = !template;
      this.count = Number(template?.dataset.packCount);
      this.copy?.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      if (this.unit) this.unit.hidden = true;
      this.queuePriceUpdate();
    }
    queuePriceUpdate() {
      if (this.queued || this.cancelled) return;
      this.queued = true;
      queueMicrotask(() => {
        this.queued = false;
        if (!this.cancelled && this.isConnected) this.updateUnitPrice();
      });
    }
    visible(node) {
      for (let current = node; current; current = current.parentElement || current.getRootNode()?.host) {
        if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;
        const style = window.getComputedStyle(current);
        if (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility)) return false;
      }
      return Boolean(node?.isConnected);
    }
    moneyFormatter(rawFormat) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = rawFormat || '';
      const normalize = (value) => String(value).replace(/[\u00a0\u202f]/g, ' ').trim();
      const format = normalize(decoder.value);
      const tokens = [...format.matchAll(/\{\{\s*(amount[a-z_]*)\s*\}\}/g)];
      if (tokens.length !== 1) return null;
      const rules = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_period_and_space_separator: [2, ' ', '.']
      };
      const token = tokens[0], rule = rules[token[1]];
      if (!rule) return null;
      const [decimals, group, decimal] = rule;
      const prefix = format.slice(0, token.index), suffix = format.slice(token.index + token[0].length);
      const escaped = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const number = '(?:[0-9]+|[0-9]{1,3}(?:' + escaped(group) + '[0-9]{3})+)';
      const matcher = new RegExp('^' + escaped(prefix) + '(' + number + (decimals ? escaped(decimal) + '[0-9]{2}' : '') + ')' + escaped(suffix) + '$');
      return {
        read: (text) => {
          const match = normalize(text).match(matcher);
          if (!match) return null;
          const parts = match[1].split(group).join('').split(decimal);
          const minor = Number(parts[0]) * 100 + (decimals ? Number(parts[1]) : 0);
          return Number.isSafeInteger(minor) && minor >= 0 ? minor : null;
        },
        write: (minor) => {
          if (!Number.isFinite(minor) || minor < 0) return null;
          const thousandths = Math.round(minor * 10);
          if (!Number.isSafeInteger(thousandths)) return null;
          const major = String(Math.floor(thousandths / 1000)).replace(/\B(?=(\d{3})+(?!\d))/g, group);
          let fraction = String(thousandths % 1000).padStart(3, '0');
          if (fraction.endsWith('0')) fraction = fraction.slice(0, 2);
          return prefix + major + decimal + fraction + suffix;
        }
      };
    }
    updateUnitPrice() {
      if (!this.unit) return;
      let value = null;
      const price = this.mainProduct.querySelector('[data-emergent-price-slot]');
      const marker = price?.querySelector('[data-emergent-price-variant]');
      if (!this.hidden && Number.isSafeInteger(this.count) && this.count > 0 && marker?.dataset.emergentPriceVariant === this.variantId) {
        const mounts = [...this.mainProduct.querySelectorAll('.nw-product-price-mount')].filter((node) => price.contains(node) || node.previousElementSibling === price);
        const visibleMounts = mounts.filter((node) => this.visible(node));
        const mount = visibleMounts.length === 1 ? visibleMounts[0] : null;
        let amountNode, moneyFormat;
        if (mount) {
          if (this.mount !== mount || this.observedShadow !== mount.shadowRoot) {
            this.shadowObserver?.disconnect();
            this.mount = mount;
            this.observedShadow = mount.shadowRoot;
            if (mount !== this.staleMount) this.awaitingShadowRefresh = false;
            if (mount.shadowRoot) {
              this.shadowObserver = new MutationObserver(() => {
                this.queuePriceUpdate();
              });
              this.shadowObserver.observe(mount.shadowRoot, {childList: true, subtree: true, characterData: true, attributes: true});
            }
          }
          const currentNodes = mount.shadowRoot?.querySelectorAll('.nw-collection-price__current');
          const current = currentNodes?.length === 1 ? currentNodes[0] : null;
          if (current !== this.staleAmountNode || current?.textContent !== this.staleAmountText) this.awaitingShadowRefresh = false;
          if (!this.awaitingShadowRefresh) {
            amountNode = current;
            moneyFormat = this.dataset.moneyFormat;
          }
        } else if (visibleMounts.length === 0 && this.visible(price)) {
          const container = price.querySelector('.price');
          const selector = container?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
          const candidates = [...price.querySelectorAll(selector)].filter((node) => !node.closest('s, del') && this.visible(node));
          if (candidates.length === 1) amountNode = candidates[0];
          moneyFormat = this.dataset.nativeMoneyFormat;
        }
        if (amountNode && this.visible(amountNode)) {
          this.amountNode = amountNode;
          const formatter = this.moneyFormatter(moneyFormat);
          const minor = formatter?.read(amountNode.textContent);
          if (minor !== null && minor !== undefined) value = formatter.write(minor / this.count);
        }
      }
      const text = value ? this.dataset.unitLabel.replace('[price]', value) : '';
      if (this.unit.textContent !== text) this.unit.textContent = text;
      this.unit.hidden = !value;
    }
  });
}
